import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Eye,
  EyeOff,
  Lock,
  KeyRound,
  CheckCircle,
  XCircle,
  Loader2,
  ShieldCheck,
  ArrowRight,
  AlertCircle,
} from "lucide-react";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [isLoading, setIsLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        toast.error("Invalid reset link", {
          description: "No reset token found in the URL",
        });
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(
          `${API_URL}/auth/verify-reset-token/${token}`
        );
        if (response.data.valid) {
          setTokenValid(true);
          setUserEmail(response.data.email);
          setUserName(response.data.name);
        } else {
          toast.error("Invalid or expired link", {
            description: response.data.detail || "This reset link is no longer valid",
          });
        }
      } catch (error) {
        console.error("Token verification error:", error);
        toast.error("Verification failed", {
          description: error.response?.data?.detail || "Failed to verify reset link",
        });
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleResetPassword = async (e) => {
    e.preventDefault();

    // Validation
    if (password.length < 8) {
      toast.error("Password too short", {
        description: "Password must be at least 8 characters long",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords don't match", {
        description: "Please make sure both passwords are identical",
      });
      return;
    }

    // Check password strength
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);

    if (!hasUpperCase || !hasLowerCase || !hasNumber || !hasSpecial) {
      toast.error("Password too weak", {
        description: "Password must contain uppercase, lowercase, number, and special character",
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/auth/reset-password`, {
        token,
        password,
      });

      toast.success("Password reset successful!", {
        description: response.data.message,
        duration: 5000,
      });

      setResetSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      console.error("Reset password error:", error);
      toast.error("Reset failed", {
        description: error.response?.data?.detail || "Failed to reset password",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const PasswordStrength = ({ password }) => {
    const checks = [
      { regex: /.{8,}/, label: "8+ characters", icon: "length" },
      { regex: /[A-Z]/, label: "Uppercase", icon: "upper" },
      { regex: /[a-z]/, label: "Lowercase", icon: "lower" },
      { regex: /[0-9]/, label: "Number", icon: "number" },
      { regex: /[^A-Za-z0-9]/, label: "Special", icon: "special" },
    ];

    const strength = checks.filter((check) => check.regex.test(password)).length;
    const strengthLabels = ["Very Weak", "Weak", "Fair", "Good", "Excellent"];
    const strengthColors = [
      "bg-red-500",
      "bg-orange-500",
      "bg-yellow-500",
      "bg-lime-500",
      "bg-green-500",
    ];

    return (
      <div className="space-y-2 mt-3">
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-600">Password strength</span>
          <span
            className={`text-xs font-medium ${
              strength >= 4
                ? "text-green-600"
                : strength >= 3
                  ? "text-yellow-600"
                  : "text-red-600"
            }`}
          >
            {strengthLabels[strength - 1] || "None"}
          </span>
        </div>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((level) => (
            <div
              key={level}
              className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                level <= strength ? strengthColors[strength - 1] : "bg-gray-200"
              }`}
            />
          ))}
        </div>
        <div className="flex flex-wrap gap-2 mt-2">
          {checks.map((check, index) => (
            <div
              key={index}
              className="flex items-center gap-1.5 text-xs"
            >
              {check.regex.test(password) ? (
                <CheckCircle size={12} className="text-green-500" />
              ) : (
                <div className="w-3 h-3 rounded-full border border-gray-300"></div>
              )}
              <span
                className={
                  check.regex.test(password) ? "text-green-600" : "text-gray-500"
                }
              >
                {check.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardContent className="pt-20 pb-20">
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-r from-primary-500 to-orange-500 flex items-center justify-center">
                <Loader2 size={32} className="text-white animate-spin" />
              </div>
              <p className="text-gray-600 text-center">Verifying reset link...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!tokenValid && !verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardContent className="pt-12 pb-12">
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle size={32} className="text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Invalid Reset Link</h2>
              <p className="text-gray-600 text-center">
                This password reset link is invalid or has expired.
                <br />
                Please request a new password reset.
              </p>
              <Button
                onClick={() => navigate("/login")}
                className="mt-4 bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600"
              >
                Back to Login
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-orange-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="w-full max-w-md border-0 shadow-xl">
            <CardContent className="pt-12 pb-12">
              <div className="flex flex-col items-center justify-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle size={32} className="text-green-500" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Password Reset Successful!</h2>
                <p className="text-gray-600 text-center">
                  Your password has been changed successfully.
                  <br />
                  Redirecting you to login...
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 size={16} className="animate-spin" />
                  <span>Please wait...</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-orange-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Card className="border-0 shadow-xl">
          <CardHeader className="space-y-3 pb-6">
            <div className="mx-auto w-14 h-14 rounded-xl bg-gradient-to-r from-primary-500 to-orange-500 flex items-center justify-center">
              <KeyRound size={24} className="text-white" />
            </div>
            <CardTitle className="text-2xl text-center font-bold bg-gradient-to-r from-primary-600 to-orange-600 bg-clip-text text-transparent">
              Reset Your Password
            </CardTitle>
            <CardDescription className="text-center text-sm">
              {userName && `Hi ${userName}, `}
              Create a new secure password for your account
            </CardDescription>
            {userEmail && (
              <div className="flex items-center justify-center gap-2 text-xs text-gray-600 bg-gray-50 py-2 px-3 rounded-lg">
                <ShieldCheck size={14} className="text-primary-500" />
                <span>{userEmail}</span>
              </div>
            )}
          </CardHeader>

          <CardContent>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                  New Password
                </Label>
                <div className="relative group">
                  <Lock
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500"
                  />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="pl-10 pr-10 h-11 border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {password && <PasswordStrength password={password} />}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                  Confirm Password
                </Label>
                <div className="relative group">
                  <Lock
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500"
                  />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="pl-10 pr-10 h-11 border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <div className="flex items-center gap-1.5 text-xs text-red-600">
                    <AlertCircle size={12} />
                    <span>Passwords don't match</span>
                  </div>
                )}
                {confirmPassword && password === confirmPassword && (
                  <div className="flex items-center gap-1.5 text-xs text-green-600">
                    <CheckCircle size={12} />
                    <span>Passwords match</span>
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-1">
                <p className="text-xs font-medium text-blue-900">Password Requirements:</p>
                <ul className="text-xs text-blue-700 space-y-0.5 ml-3">
                  <li>• At least 8 characters long</li>
                  <li>• Contains uppercase and lowercase letters</li>
                  <li>• Contains at least one number</li>
                  <li>• Contains at least one special character</li>
                </ul>
              </div>

              <Button
                type="submit"
                disabled={
                  isLoading ||
                  !password ||
                  !confirmPassword ||
                  password !== confirmPassword ||
                  password.length < 8
                }
                className="w-full h-11 bg-gradient-to-r from-primary-500 to-orange-500 hover:from-primary-600 hover:to-orange-600 text-white font-medium shadow-lg hover:shadow-xl transition-all duration-200"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin" />
                    Resetting Password...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Reset Password
                    <ArrowRight size={16} />
                  </span>
                )}
              </Button>

              <div className="text-center pt-2">
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="text-sm text-gray-600 hover:text-primary-600 transition-colors"
                >
                  Back to Login
                </button>
              </div>
            </form>
          </CardContent>
        </Card>

        <div className="mt-4 p-3 rounded-lg bg-white/50 backdrop-blur-sm border border-gray-200">
          <div className="flex items-start gap-2 text-xs text-gray-600">
            <ShieldCheck size={14} className="text-primary-500 mt-0.5 flex-shrink-0" />
            <p>
              <strong>Security Note:</strong> This link will expire in 1 hour and can only be used once.
              After resetting your password, you'll need to log in with your new credentials.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
